/* eslint-disable jira/filename-case */
import React, { memo, useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Inline } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	SCREEN,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { FilterRefinement$key } from '@atlassian/jira-relay/src/__generated__/FilterRefinement.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { AstContainer, useAnonymizedJql, useHasJql } from '../controllers/ast/index.tsx';
import type { ContainerProps } from '../controllers/ast/types.tsx';
import { useNumberOfSelectedFieldsWithFG } from '../controllers/ast/use-number-of-selected-fields/index.tsx';
import { useNumberOfSelectedValues } from '../controllers/ast/use-selected-values/index.tsx';
import { SOURCE_NAME, TEAM_NAME, PACKAGE_NAME } from '../common/constants.tsx';
import { AssigneePicker } from './assignee-picker/AssigneePicker.tsx';
import { FilterPopup } from './filter-popup/index.tsx';
import SearchField from './search-field/index.tsx';
import type { Props } from './types.tsx';
import ErrorComponent from './error/index.tsx';

const FilterRefinementWithErrorBoundary = ({
	debounce = 500,
	defaultJql,
	onSearch,
	...props
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnSearch = useCallback<Required<ContainerProps>['onJqlChange']>(
		(updateJql: string, fieldType?: string, amountOfSelectedOptionOfFieldType?: number) => {
			onSearch && onSearch(updateJql);
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'changed',
				actionSubject: 'filterRefinement',
				actionSubjectId: 'changedFilterRefinement',
				attributes: {
					source: SOURCE_NAME,
					fieldType,
					amountOfSelectedOptionsOfFieldType: amountOfSelectedOptionOfFieldType,
				},
			});
		},
		[onSearch, createAnalyticsEvent],
	);

	const [handleSearchDebounced] = useDebouncedCallback(handleOnSearch, debounce);

	return (
		<JSErrorBoundary
			id={SOURCE_NAME}
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			sendToPrivacyUnsafeSplunk
			fallback={() => <ErrorComponent />}
		>
			<AstContainer
				defaultJql={defaultJql}
				onJqlChange={handleSearchDebounced}
				isModeSwitcherEnabled={
					fg('list_lovability_improving_filters') ? props.isModeSwitcherEnabled : false
				}
			>
				<FilterRefinement {...props} defaultJql={defaultJql} />
			</AstContainer>
		</JSErrorBoundary>
	);
};

const FilterRefinement = ({
	defaultJql,
	enablePostOfficeTour = false,
	fragmentRef,
	isAddPeopleButtonEnabled,
	projectId,
	projectKey,
	projectName,
	scope,
	searchFieldPlaceholder,
	showAssigneeFilter = true,
	changeboardingMessageId,
	isSoftwareFullTheming,
	isResponsive = true,
	isModeSwitcherEnabled = false,
	projectContext,
}: Props) => {
	const data = useFragment<FilterRefinement$key>(
		graphql`
			fragment FilterRefinement on JiraQuery
			@argumentDefinitions(cloudId: { type: "ID!" }, scope: { type: "JiraJqlScopeInput" }) {
				...AssigneePicker @arguments(cloudId: $cloudId, scope: $scope)
			}
		`,
		fragmentRef,
	);

	const numberOfSelectedValues = useNumberOfSelectedValues(
		fg('list_lovability_improving_filters') ? projectContext : undefined,
	);
	const anonymizedJql = useAnonymizedJql();
	const hasJql = useHasJql();
	const numberOfSelectedFields = useNumberOfSelectedFieldsWithFG();

	const analyticsAttributes = useMemo(
		() => ({
			isAssigneeFilterRendered: showAssigneeFilter,
			numberOfSelectedValues,
			changeboardingMessageId,
			...(fg('filter_refinement_jql_analytics')
				? {
						anonymizedJql,
						hasJql,
						numberOfSelectedFields,
					}
				: {}),
			...(fg('list_lovability_improving_filters') ? { isModeSwitcherEnabled } : {}),
		}),
		[
			showAssigneeFilter,
			numberOfSelectedValues,
			changeboardingMessageId,
			anonymizedJql,
			hasJql,
			numberOfSelectedFields,
			isModeSwitcherEnabled,
		],
	);

	return (
		<UFOSegment name="filter-refinement">
			<ContextualAnalyticsData
				sourceName={SOURCE_NAME}
				sourceType={SCREEN}
				attributes={
					fg('filter_refinement_jql_analytics')
						? analyticsAttributes
						: { ...analyticsAttributes, isDefaultJql: defaultJql !== undefined }
				}
			>
				<Inline alignBlock="center" space="space.075">
					<SearchField inputLabel={searchFieldPlaceholder} />
					{showAssigneeFilter && (
						<AssigneePicker
							fragmentRef={data}
							isAddPeopleButtonEnabled={isAddPeopleButtonEnabled}
							projectKey={projectKey}
							projectId={projectId}
							projectName={projectName}
							isResponsive={isResponsive}
							{...(fg('list_lovability_improving_filters') && { isModeSwitcherEnabled })}
						/>
					)}
					<FilterPopup
						scope={scope}
						changeboardingMessageId={changeboardingMessageId}
						enablePostOfficeTour={enablePostOfficeTour}
						isSoftwareFullTheming={isSoftwareFullTheming}
						{...(fg('list_lovability_improving_filters') && {
							isModeSwitcherEnabled,
							projectContext,
						})}
					/>
				</Inline>
				<FireUiAnalytics
					actionSubject="screen"
					action="viewed"
					actionSubjectId="filterRefinement"
				/>
			</ContextualAnalyticsData>
		</UFOSegment>
	);
};

export default memo(FilterRefinementWithErrorBoundary);
